import { useState } from "react";
import { Sparkles, Users, Box, ShieldCheck, BarChart3, Radio } from "lucide-react";

const FeaturesSection = () => {
  const [activeIndex, setActiveIndex] = useState(1);

  return (
    <section id="features" className="p-10 md:py-14 md:px-[120px] bg-dark-grey relative">
      <div className="container relative z-10">

        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          onMouseLeave={() => setActiveIndex(0)} // revert to first card
        >
          {[
            {
              icon: <Sparkles className="h-5 w-5" />,
              title: "AI-Powered Matchmaking",
              description:
                "Intelligent crew and equipment matching based on your project requirements, location, and budget.",
            },
            {
              icon: <Users className="h-5 w-5" />,
              title: "Crew Management",
              description:
                "Organize your team, assign tasks, track progress, and communicate seamlessly in one platform.",
            },
            {
              icon: <Box className="h-5 w-5" />,
              title: "Equipment Tracking",
              description:
                "Manage inventory across locations, schedule maintenance, and optimize equipment utilization.",
            },
            {
              icon: <Radio className="h-5 w-5" />,
              title: "Integrated Live Streaming",
              description:
                "Stream directly to Twitch, YouTube, Facebook, and more. All from one unified control center.",
            },
            {
              icon: <BarChart3 className="h-5 w-5" />,
              title: "Analytics & Insights",
              description:
                "Track performance, revenue, crew efficiency, and equipment ROI with detailed analytics.",
            },
            {
              icon: <ShieldCheck className="h-5 w-5" />,
              title: "Enterprise Security",
              description:
                "Bank-level security, role-based access, and complete audit trails for peace of mind.",
            },
          ].map((card, i) => (
            <FeatureCard
              key={i}
              {...card}
              active={activeIndex === i}
              onHover={() => setActiveIndex(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturesSection;

const FeatureCard = ({ icon, title, description, active, onHover }) => (
  <div
    onMouseEnter={onHover}
    className="group relative p-[1px] rounded-xl transition-all duration-500"
  >
    {/* Gradient border */}
    <div
      className={`
        absolute inset-0 rounded-xl
        bg-gradient-to-tr from-golden/40 via-golden/10 to-transparent
        transition-opacity duration-500
        ${active ? "opacity-100" : "opacity-0"}
      `}
    />

    {/* Card background */}
    <div className="relative p-4 rounded-xl bg-[#080808] transition-all duration-500 overflow-hidden h-full">

      {/* Hover overlay */}
      <div
        className={`
          pointer-events-none absolute inset-0 rounded-xl
          bg-gradient-to-bl from-golden/35 via-golden/10 via-[20%] to-transparent to-[45%]
          transition-opacity duration-500
          ${active ? "opacity-100" : "opacity-0"}
        `}
      />

      {/* Icon */}
      <div className="
        relative h-10 w-10 rounded-full flex items-center justify-center mb-6
        bg-white/5 transition-all duration-500 overflow-hidden
      ">

        <div
          className={`
            absolute inset-0 rounded-full
            bg-gradient-to-tr from-golden/80 to-transparent
            transition-opacity duration-500
            ${active ? "opacity-100" : "opacity-0"}
          `}
        />

        <div className={`relative z-10 transition-colors duration-500 ${active ? "text-golden" : "text-white/70"}`}>
          {icon}
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-3 text-white tracking-wide relative z-10">
        {title}
      </h3>

      <p className="text-base text-zinc-400 leading-relaxed font-normal relative z-10">
        {description}
      </p>
    </div>
  </div>
);
